import { Injectable, Injector, Renderer2 } from '@angular/core'
import { DynamicElementSpecification } from '../../../models/dynamic-element/dynamic-element-specification'
import { DynamicElementInterceptor } from '../dynamic-element-interceptor'

/**
 * Configuration for the {@link DataToTextInterceptor}
 */
export interface DataToTextInterceptorConfig {
  /**
   * Name of the data property that will be used for the text content.
   */
  name: string
}

/**
 * Convert an incoming data property as the text content of the element
 */
@Injectable({ providedIn: 'root' })
export class DataToTextInterceptor implements DynamicElementInterceptor {

  private readonly _lineBreakPattern = /<br\s*\/?>/gi
  private readonly _lineBreakSubstitution = '\n'
  private readonly _domParser: DOMParser = new DOMParser()

  afterBuild(injector: Injector, args: DynamicElementSpecification, element: HTMLElement, config?: unknown): HTMLElement {
    let renderer2 = injector.get<Renderer2>(Renderer2)

    this.validateConfig(config)

    let value = args.data?.[config.name] as string ?? ''
    value = value.replace(this._lineBreakPattern, this._lineBreakSubstitution)
    value = this._domParser
      .parseFromString(value, 'text/html')
      ?.documentElement
      ?.textContent
      ?.trim() ?? ''
    value = value.replace(this._lineBreakPattern, this._lineBreakSubstitution)

    renderer2.appendChild(element, renderer2.createText(value))
    return element
  }

  /**
   * Validate the configuration of the interceptor.
   *
   * Ensures that the config matches the type {@link DataToTextInterceptorConfig }.
   *
   * @internal
   *
   * @param config  The config for the interceptor
   */
  validateConfig(config: unknown): asserts config is DataToTextInterceptorConfig {
    if (typeof config == 'object') {
      let pendingConfig = config as Record<string, unknown>
      if (typeof pendingConfig?.name === 'string') {
        return
      }
    }

    throw new Error('Configs provided for DataToTextInterceptor are invalid. Configs must match type { name: string }')
  }

}
